import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Box, List, ListItemButton, ListItemIcon, ListItemText, Collapse } from "@mui/material";
import { ExpandLess, ExpandMore } from "@mui/icons-material";
import { grey } from "@mui/material/colors";
import { rootProtectedPath } from "data/authData";

const DrawerListItem = ({
	service,
	currentPage,
	open,
	nested = false,
	depth = 0,
	listSX = {},
	parentPath = "",
	navBasePath = `/${rootProtectedPath}`,
}) => {
	const [dropDown, setDropDown] = useState(false);
	const navigate = useNavigate();
	const itemRef = useRef(null);

	// Check if service has visible children
	const hasVisibleChildren = service?.types && Object.values(service.types).some((child) => child.display);

	const fullPath = parentPath ? `${parentPath}/${service.key}` : `${service.key}`;

	const handleToggleDropdown = () => {
		setDropDown((prev) => {
			const newValue = !prev;
			if (newValue && itemRef.current) {
				setTimeout(() => {
					itemRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
				}, 100);
			}
			return newValue;
		});
	};

	const handleNavigate = () => {
		const base = (navBasePath || `/${rootProtectedPath}`).replace(/\/$/, "");
		navigate(`${base}/${fullPath}`);
	};

	const handleClick = (e) => {
		if (hasVisibleChildren) handleToggleDropdown(e);
		else handleNavigate(e);
	};

	// Auto-open if currentPage is a child, and scroll into view
	useEffect(() => {
		const isCurrentInThisNodeOrChildren = (svc) => {
			if (svc.key === currentPage) return true;
			if (!svc.types) return false;
			return Object.values(svc.types).some((child) => isCurrentInThisNodeOrChildren(child));
		};

		const isCurrent = (svc) => svc.key === currentPage;
		// If current item is somewhere within this node, expand it
		if (hasVisibleChildren && isCurrentInThisNodeOrChildren(service)) {
			setDropDown(true);
		}
		// If this is the currently selected item, scroll into view after open
		if (isCurrent(service)) {
			const scrollToItem = () => {
				requestAnimationFrame(() => {
					const el = itemRef.current;
					const rect = el.getBoundingClientRect();
					const isInView = rect.top >= 0 && rect.bottom <= window.innerHeight;
					if (!isInView) {
						el.scrollIntoView({ behavior: "smooth", block: "center" });
					}
				});
			};
			if (nested) {
				// Wait a bit if in nested dropdown (allow time for animation/render)
				setTimeout(scrollToItem, 350); // ensure Collapse has finished opening
			} else {
				scrollToItem();
			}
		}
	}, []);

	return (
		<Box ref={itemRef} overflow="hidden" width="100%">
			<ListItemButton
				onClick={handleClick}
				selected={currentPage === service.key}
				sx={{
					height: nested ? 40 : "auto",
					pl: nested && depth ? depth + 1 : 2,
				}}>
				<ListItemIcon sx={nested ? { transform: "scale(0.8)", minWidth: "45px" } : { minWidth: "45px" }}>
					{service?.icon}
				</ListItemIcon>
				<ListItemText
					primary={service.name?.toLowerCase()}
					primaryTypographyProps={{
						fontWeight: "medium",
						variant: nested ? "body2" : "body1",
						noWrap: true,
						textTransform: "capitalize",
					}}
				/>
				{hasVisibleChildren && (dropDown ? <ExpandLess /> : <ExpandMore />)}
			</ListItemButton>

			{hasVisibleChildren && (
				<Collapse in={dropDown} timeout="auto" sx={{ background: grey[depth * 100 + 100] || grey[100] }}>
					<List disablePadding sx={listSX}>
						{Object.entries(service.types)
							.filter(([, child]) => child.display)
							.map(([key, child]) => (
								<DrawerListItem
									key={key}
									service={child}
									currentPage={currentPage}
									open={open}
									nested={true}
									depth={depth + 1}
									parentPath={fullPath}
									navBasePath={navBasePath}
								/>
							))}
					</List>
				</Collapse>
			)}
		</Box>
	);
};

export default DrawerListItem;
